/**
 * Set similarity between two lists of small records — the one scoring primitive the browser
 * still owns.
 *
 * It is used for DISPLAY only: `ValueAndPropertyDisplay` compares a questionnaire's score series
 * against the validated one to colour a cell. Nothing here decides a quality number.
 *
 * What used to live beside it, and why it is gone: a full `evaluatePrecision` with a hardcoded
 * `FIELD_CONFIG` of rachis fields. It scored EVERY configured field for every patient, so a field
 * the doctor had never validated read as a miss and entered the mean as a zero — the exact defect
 * `precision._is_scorable` was written to end on the backend. It had no caller left, and a second
 * scorer that disagrees with the real one is not dead weight, it is a trap: the next person to
 * need a number in the browser would have rewired it.
 *
 * The rule, in one line: an experiment scores the fields a clinician answered for — a validated
 * value, or an explicit clear — and the backend is the only place that decides it. A page that
 * needs a score asks the API for it.
 */

export type DeepSimilarityInput = Record<string, string | number>;

function freezeDict(obj: DeepSimilarityInput) {
  // Turn object into a sorted string of key=value pairs
  const entries = Object.entries(obj);
  entries.sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

function calculateF1(precision: number, recall: number): number {
  if (precision + recall === 0) return 0;
  return (2 * precision * recall) / (precision + recall);
}

/**
 * F1 between two multisets of records, matched on their full content.
 *
 * Two empty lists are a perfect match (both say "nothing"); one empty against a non-empty one is
 * a total mismatch. Counts matter: the same record twice on one side and once on the other is a
 * partial match, not a full one.
 */
export function deepSimilarity(
  truth: DeepSimilarityInput[],
  guessed: DeepSimilarityInput[],
  debug = false,
) {
  if ((!truth || truth.length === 0) && (!guessed || guessed.length === 0)) {
    return 1.0;
  }
  if (!truth || !guessed || truth.length === 0 || guessed.length === 0) {
    return 0.0;
  }

  const truthCounts: Record<string, number> = {};
  const guessedCounts: Record<string, number> = {};

  for (const x of truth) {
    const key = freezeDict(x);
    truthCounts[key] = (truthCounts[key] || 0) + 1;
  }

  for (const x of guessed) {
    const key = freezeDict(x);
    guessedCounts[key] = (guessedCounts[key] || 0) + 1;
  }

  // intersection: min count for each key
  let matches = 0;
  for (const key of Object.keys(truthCounts)) {
    if (guessedCounts[key]) {
      matches += Math.min(truthCounts[key], guessedCounts[key]);
    }
  }

  const truthTotal = Object.values(truthCounts).reduce((a, b) => a + b, 0);
  const guessedTotal = Object.values(guessedCounts).reduce((a, b) => a + b, 0);

  const precision = matches / guessedTotal;
  const recall = matches / truthTotal;
  const f1 = calculateF1(precision, recall);

  if (debug) {
    console.warn("truth:", truthCounts);
    console.warn("guessed:", guessedCounts);
    console.warn({ matches, precision, recall, f1 });
  }

  return Number(f1.toFixed(4));
}
